import create from "zustand";
import {
  AGREE_ON_TERMS_STORAGE,
  ConversationDDResult,
  PatientStateFields,
} from "./useCreatePatientStateProvider";
import { dispatchKahunEvent, KahunApiEvents } from "../events/events";
import {
  ConversationDriverExchange,
  ConversationStatus,
  DriverDebugData,
  ExchangeAnswer,
  ExchangeResponse,
} from "../driver/types/driverTypes";
import {
  ConversationQuestionType as QTypes,
  ConversationQuestionType,
  SortType,
} from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import {
  END_OF_CHAT_PROMPT_FEATURE_ID,
  FREE_TEXT_FEATURE_ID,
  GenderValue,
} from "shared";
import { isProduction } from "fe-common/utils/envGetter";
import { getVersion } from "../VersionInfo";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import { featuresToMultiLine } from "fe-common/logic";
import {
  ConversationDriverEventListener,
  ConversationDriverImpl,
} from "../driver/ConversationDriver";
import { restoreConversationFromStorage } from "../solverHooks/saveConversation";
import { demoPaneStateManager } from "./stateManagers/demoPaneStateManager";
import {
  GENDER_MALE_FEATURE_ID,
  RANGE_QUALIFIER_FEATURE_ID,
} from "fe-common/graphql/dao";
import { symptomQualifierTxt } from "../components/uiControllers/SymptomText";
import { getCurrentTime } from "fe-common/utils/time";

type CreatePatientStateConfig = {
  isDebug: boolean;
  driver: ConversationDriverImpl;
  partnerId: string;
  finishConversation: (skipRedirect?: boolean) => any;
  sessionId: string;
  caseId: string;
  clinicId: string;
  clinicName: string;
  lang: string;
  conversationId?: string;
  agreeOnTerms: boolean;
  nonPersist?: boolean;
};

type SetState = (state: Partial<PatientStateFields>) => void;
type GetState = () => PatientStateFields;

function lastQuestionToText(exchange: ConversationDriverExchange) {
  const type = exchange.type;
  const lastQuestion = exchange.question.question;

  if (type !== QTypes.Suggestion && type !== QTypes.BackgroundSuggestion) {
    return type;
  }
  if (lastQuestion?.suggestions?.length > 1) {
    return "Do you have any of the following symptoms?";
  }

  if (lastQuestion?.suggestions[0]?.question?.text === "Free text") {
    return lastQuestion.question.text + " (Free text)";
  }
  return lastQuestion?.suggestions[0]?.question?.text || "";
}

export const createPatientState = (config: CreatePatientStateConfig) => {
  const {
    driver,
    partnerId,
    finishConversation,
    sessionId,
    caseId,
    clinicId,
    clinicName,
    conversationId,
    agreeOnTerms,
    nonPersist,
  } = config;

  function getUrlForReport() {
    const caseId = driver.getCaseId();
    return `https://editor-dev.kahun.com/#/w-kahun/wconversationcase/${caseId}`;
  }

  function persistsAgreeOnTerms(agreeOnTerms) {
    sessionStorage.setItem(
      sessionId + "_" + AGREE_ON_TERMS_STORAGE,
      agreeOnTerms ? "true" : "",
    );
  }

  let pendingExchangeExists: boolean = false;

  const storageData = restoreConversationFromStorage(sessionId || "");

  const initialState = {
    stateExchanges: [],
    conversationDDResult: {
      ddClusters: { top: [], others: [] },
      crs: [],
      urgency: null,
      navigationRecommendation: null,
    },
    elementIndex: null,
    agreeOnTerms,
    loading: true,
    loadingUndo: false,
    masterLoading: false,
    selectedSuggestion: null,
    selectedDiseaseReasoning: null,
    showTriageReasoning: false,
    conversationDebug: null,
    gender: null,
    showFeedbackPopup: false,
  };

  if (agreeOnTerms) {
    persistsAgreeOnTerms(agreeOnTerms);
  }

  const setExchanges = (set: SetState) => (
    stateExchanges: ConversationDriverExchange[],
  ) => set({ stateExchanges });

  const setGender = (set: SetState, get: GetState) => {
    return (gender: GenderValue) => {
      if (get().gender !== gender) return set({ gender });
      return null;
    };
  };

  const isEndOfChatExchange = (
    exchange: ConversationDriverExchange,
  ): boolean => {
    return !exchange.question?.question && !exchange.hasMore;
  };

  const setExchange = (set: SetState, get: GetState) => (
    exchange: ConversationDriverExchange,
  ) => {
    const exchanges = get().stateExchanges;
    const total = exchanges.length;
    const status = driver.getConversationStatus();

    const url = getUrlForReport();
    reportStatsOnExchange(url);

    if (isEndOfChatExchange(exchange)) {
      reportStatsFinalExchange(url, status, exchange, exchanges);
      //Finish the conversation if there are no more question or no more pending info messages
      if (!get().elementIndex) {
        finishConversation();
      }
    }

    updateExchangeToState();

    function updateExchangeToState() {
      if (exchanges[exchanges.length - 1].id === exchange.id) {
        set({
          stateExchanges: [
            ...exchanges.slice(0, exchanges.length - 1),
            exchange,
          ],
        });
      } else {
        set({ stateExchanges: [...exchanges, exchange] });
      }
    }

    function reportStatsOnExchange(url: string) {
      // console.log("Prevented report state on exchange");
    }

    function reportStatsFinalExchange(
      url: string,
      status: ConversationStatus,
      exchange: ConversationDriverExchange,
      exchanges: ConversationDriverExchange[],
    ) {
      try {
        let visitType;
        const visitTypeEx = exchanges?.find(
          (x) => x?.question?.question?.questionType === QTypes.ReasonForVisit,
        );
        const prevExchange = exchanges?.slice(0, -1)?.pop();
        const lastExchange = exchanges?.slice(-1)?.pop();
        if (visitTypeEx) {
          visitType = qfsToAnswerText(
            visitTypeEx.answer?.qfs,
            visitTypeEx.type,
          ).trim();
        }
        evt({
          category: CATEGORY.PATIENT_GENERAL,
          action:
            status === ConversationStatus.ABANDONED
              ? ACTION.PATIENT_SKIPPED_CONVERSATION
              : ACTION.PATIENT_FINISH_CONVERSATION,
          partnerId,
          props: {
            questionNumber: total,
            url,
            clinicId,
            clinicName,
            duration:
              (prevExchange || exchange)?.question?.question?.elapsed /
              1000 /
              60,
            visitType,
            infoText: (exchange.info || lastExchange.info)?.[0]?.infoText,
            infoId: (exchange.info || lastExchange.info)?.[0]?.id,
          },
        });
      } catch (e) {
        console.error(e);
        evt({
          category: CATEGORY.PATIENT_GENERAL,
          action:
            status === ConversationStatus.ABANDONED
              ? ACTION.PATIENT_SKIPPED_CONVERSATION
              : ACTION.PATIENT_FINISH_CONVERSATION,
          label: "error while reporting: -" + JSON.stringify(e, null, 2),
        });
      }
    }
  };

  const setElementIndex = (set: SetState) => (elementIndex: number) =>
    set({ elementIndex });

  const setLoading = (set: SetState) => (loading: boolean) => set({ loading });
  const setLoadingUndo = (set: SetState) => (loadingUndo: boolean) =>
    set({ loadingUndo });
  const setMasterLoading = (set: SetState) => (masterLoading: boolean) =>
    set({ masterLoading });
  const setConversationDebug = (set: SetState) => (
    conversationDebug: DriverDebugData,
  ) => set({ conversationDebug });

  const setAgreeOnTerms = (set: SetState) => (agreeOnTerms: boolean) => {
    persistsAgreeOnTerms(agreeOnTerms);
    return set({ agreeOnTerms });
  };

  const setAbandoned = (set: SetState, get: GetState) => async (
    immediate: boolean,
  ) => {
    setLoading(set)(true);
    setMasterLoading(set)(true);
    const res = await driver.abandone(immediate);
    setLoading(set)(false);
    setMasterLoading(set)(false);
    if (immediate) {
      finishConversation(true);
    } else {
      setElementIndex(set)(null);
      setExchangeAndDebugToState(set, get, res);
    }
  };

  const setShowFeedbackPopup = (set: SetState) => (status: boolean) => {
    set({ showFeedbackPopup: status });
  };

  const setConversationDDResult = (set: SetState) => (
    ddResults: ConversationDDResult,
  ) => {
    const { ddClusters: clusters } = ddResults;

    set({
      conversationDDResult: {
        ...ddResults,
        ddClusters: {
          top: clusters.filter((cluster) =>
            cluster.cr.inTopCRPerSortType.includes(SortType.PatientProb),
          ),
          others: clusters.filter(
            (cluster) =>
              !cluster.cr.inTopCRPerSortType.includes(SortType.PatientProb),
          ),
        },
      },
    });
  };

  const undo = (set: SetState, get: GetState) => async () => {
    setLoadingUndo(set)(true);
    //CHEN TODO move this to the driver?
    const exchanges = get().stateExchanges;
    const lastAnswerExchange = exchanges[getLastAnswerIndex()];
    const newExchange = {
      ...lastAnswerExchange,
      answer: null,
      previousAnswer: lastAnswerExchange.answer,
      info: null,
      urgencyLevel: null,
      error: null,
    };

    //call server undo only if the last exchange wasn't answer, which means we need a new context
    //If last exchange has answer, just remove it.
    if (!exchanges[exchanges.length - 1].answer) {
      await driver.undo(newExchange);
    }

    reportUndoEvent(lastAnswerExchange);

    const exchangesToRemoveIndex = exchanges.findIndex(
      (e) => e.id === newExchange.id,
    );

    const stateExchanges = [
      ...exchanges.slice(0, exchangesToRemoveIndex),
      newExchange,
    ];
    set({
      stateExchanges,
      elementIndex: stateExchanges[stateExchanges.length - 1]?.info?.length
        ? stateExchanges.length
        : null,
      loadingUndo: false,
    });

    function getLastAnswerIndex() {
      let lastAnsweredIndex = -1;
      for (let i = get().stateExchanges.length - 1; i >= 0; i--) {
        if (get().stateExchanges[i].answer) {
          lastAnsweredIndex = i;
          break;
        }
      }
      return lastAnsweredIndex;
    }

    function reportUndoEvent(lastAnswerExchange: ConversationDriverExchange) {
      evt({
        category: CATEGORY.PATIENT_CHAT,
        action: ACTION.PATIENT_UNDONE_ANSWER,
        partnerId,
        props: {
          clinicId,
          clinicName,
          question: lastQuestionToText(lastAnswerExchange),
          answer: `${qfsToAnswerText(
            lastAnswerExchange.answer.qfs,
            lastAnswerExchange?.type,
          )}${lastAnswerExchange.answer.skip ? "skipped" : ""})`,
        },
      });
    }
  };

  const completeInfoMessages = (set: SetState, get: GetState) => {
    return () => {
      if (pendingExchangeExists) {
        setLoading(set)(true);
      }

      const prevElementIndex = get().elementIndex;
      setElementIndex(set)(null);
      //if this is the last message finish the conversation
      const lastExchange = get().stateExchanges[
        get().stateExchanges.length - 1
      ];
      if (prevElementIndex && isEndOfChatExchange(lastExchange)) {
        finishConversation();
      }
    };
  };

  async function setPendingExchanges(
    set: SetState,
    get: GetState,
    res: ExchangeResponse,
  ) {
    let exchange = res.exchange;
    while (exchange.hasMore) {
      pendingExchangeExists = true;
      const exchangeResponse = await driver.getNextResponse(!nonPersist);
      exchange = exchangeResponse.exchange;

      setExchangeAndDebugToState(set, get, exchangeResponse);

      if (exchange.error) {
        await sendFeedbackOnError(exchange);
      }
    }
    // if we called getNextResponse at least once, update with the last received context
    setLoading(set)(false);
    pendingExchangeExists = false;
  }

  const onAnswer = (set: SetState, get: GetState) => async (
    answer: ExchangeAnswer,
  ) => {
    setLoading(set)(true);

    addAnswerToCurrentExchange();

    const answeredExchange = driver.getCurrentExchange();

    // Before waiting for response, it that's a gender question we are answering,
    // update the gender in the state without waiting for the server
    let gender = answeredExchange?.gender;
    if (answeredExchange.type === ConversationQuestionType.Gender) {
      gender = answer.qfs?.[0].qualifiers?.[0]?.value?.id as GenderValue;
      setGender(set, get)(gender);
    }

    reportAnswer(
      answeredExchange,
      getUrlForReport(),
      get().stateExchanges.length,
    );

    const res = await driver.answer(answer, !nonPersist);

    dispatchKahunEvent(KahunApiEvents.status, {
      status: ConversationStatus.IN_PROGRESS, //report in progress after every answer, COMPLETE/ABANDONED is reported when conversation is finalized
      progress: answeredExchange?.question?.progress,
      gender: get().gender
        ? get().gender === GENDER_MALE_FEATURE_ID
          ? "Male"
          : "Female"
        : undefined,
    });

    setLoading(set)(false);

    if (res.exchange.hasMore) pendingExchangeExists = true;

    setExchangeAndDebugToState(set, get, res);

    if (res.exchange.error) {
      await sendFeedbackOnError(res.exchange);
    }

    if (res.exchange.hasMore && res.exchange.info?.some((i) => i.confirm)) {
      setElementIndex(set)(get().stateExchanges.length);
    }

    // Update gender if it's missing in our state
    if (res.gender && res.gender !== get().gender) {
      setGender(set, get)(res.gender);
    }

    setPendingExchanges(set, get, res);

    function addAnswerToCurrentExchange() {
      const exchanges = get().stateExchanges;
      const lastExchange = exchanges[exchanges.length - 1];
      const exchangeWithAnswer = { ...lastExchange, answer };

      set({
        stateExchanges: [
          ...exchanges.slice(0, exchanges.length - 1),
          exchangeWithAnswer,
        ],
      });
    }

    function reportAnswer(
      exchange: ConversationDriverExchange,
      url,
      questionNumber,
    ) {
      const blockPii =
        exchange?.question?.question?.questionType === QTypes.Form ||
        exchange?.question?.question?.questionType ===
          ConversationQuestionType.FreeText;

      const qfText = qfsToAnswerText(answer.qfs, exchange.type);

      const answerText =
        (blockPii ? "PII DATE BLOCKED" : qfText.trim()) +
        (answer.skip ? "skipped" : "");

      evt({
        category: CATEGORY.PATIENT_CHAT,
        action: ACTION.PATIENT_ANSWERED_QUESTION,
        label: ``,
        partnerId,
        props: {
          question: lastQuestionToText(exchange),
          answer: answerText,
          questionNumber,
          mi:
            exchange.question.question?.suggestions?.[0]?.mutualInformation?.toFixed?.(
              2,
            ) || 1,
          progress: exchange.question.progress,
          desc: exchange.question?.question?.description || "",
          duration: exchange.question?.question?.elapsed / 1000 / 60 || 0,
          url,
          clinicId,
          clinicName,
        },
      });
    }
  };

  async function sendFeedbackOnError(exchange: ConversationDriverExchange) {
    if (isProduction() && exchange.error) {
      const text = `Auto-feedback - Patient 1st load error - v${getVersion()}\n userAgent:${
        window?.navigator?.userAgent
      }\ntime=${getCurrentTime()}\nerror=${exchange.error}`;

      const userName = await getCurrentUserEmail();
      await driver.sendFeedback(text, userName);
    }
  }

  function setExchangeAndDebugToState(
    set: SetState,
    get: GetState,
    p: ExchangeResponse,
  ) {
    setExchange(set, get)(p.exchange);
    setConversationDebug(set)(p.debug);
  }

  const onRetry = (set: SetState, get: GetState) => async () => {
    setLoading(set)(true);
    const exchanges = get().stateExchanges;
    const lastAnswerExchange = exchanges[exchanges.length - 1];
    const res = await driver.retry();
    res.exchange.previousAnswer = lastAnswerExchange.answer;
    setExchangeAndDebugToState(set, get, res);
    if (res.exchange.error) {
      await sendFeedbackOnError(res.exchange);
    }
    setLoading(set)(false);

    setPendingExchanges(set, get, res);
  };

  const driverEventListener: ConversationDriverEventListener = {
    contextChanged: (context) => {
      dispatchKahunEvent(KahunApiEvents.debug, {
        context: context,
        settings: driver.getCurrentSettings(),
      });
    },
  };
  const init = (set: SetState, get: GetState) => (
    agreeOnTerms: boolean,
    isNewLocale: boolean,
  ) => {
    if (agreeOnTerms && get().agreeOnTerms !== agreeOnTerms) {
      set({ agreeOnTerms });
    }
    if (!get().stateExchanges?.length || isNewLocale) {
      setLoading(set)(true);

      // set callback to driver
      driver.addEventListener(driverEventListener);

      if (isNewLocale) {
        // console.log("init from locale", driver.getCaseId());
        // // A hack to get the stack trace
        // console.error(new Error(""));
        if (!driver.getCaseId()) return;
      }

      driver
        .init({
          sessionId,
          patientCaseId: caseId || (isNewLocale ? driver.getCaseId() : null),
          debug: config.isDebug,
          storageData,
          conversationId,
          clinicId,
        })
        .then(({ exchanges, debug, gender }) => {
          const prevExchange = exchanges[exchanges.length - 2];
          const exchange = exchanges[exchanges.length - 1];
          if (exchange.hasMore) pendingExchangeExists = true;
          //if previous exchange has info messages don't assume they were approved.
          if (
            !exchange.answer &&
            prevExchange &&
            prevExchange.info?.some((i) => i.confirm)
          ) {
            setElementIndex(set)(exchanges.length - 1);
          }
          setExchanges(set)(exchanges);
          setConversationDebug(set)(debug);
          setPendingExchanges(set, get, {
            exchange,
            debug,
          });
          setGender(set, get)(gender);

          if (exchange.hasMore && exchange.info?.some((i) => i.confirm)) {
            setElementIndex(set)(exchanges.length - 1);
          }
        })
        .finally(() => setLoading(set)(false));
    } else {
      setLoading(set)(false);
    }
  };

  /***  Computed Fields  ***/
  //https://github.com/pmndrs/zustand/issues/132
  const chiefComplaint = (get: GetState) => {
    const ccExchange = get().stateExchanges?.find(
      (e) =>
        e.type === ConversationQuestionType.ChiefComplaint ||
        e.type === ConversationQuestionType.MultipleChiefComplaint,
    );

    return ccExchange?.answer?.qfs?.length ? ccExchange?.answer?.qfs[0] : null;
  };

  const age = (get: GetState) => {
    const ageExchange = get().stateExchanges?.find(
      (e) => e.type === ConversationQuestionType.Age,
    );
    return ageExchange?.answer?.qfs?.length
      ? ageExchange.answer.qfs[0].qualifiers[0].range.low
      : null;
  };

  const exchanges = (get: GetState) => {
    //Has initial messages, show only the first (info messages only) exchange
    if (get().elementIndex === 0) {
      return get().stateExchanges.slice(0, 1);
    }

    return get().elementIndex > 0
      ? get().stateExchanges.slice(0, get().elementIndex)
      : get().stateExchanges;
  };

  const progress = (get: GetState) => {
    // Calculate progress based on the last answered exchange
    const exgs = get().stateExchanges;
    const lastAnsweredExchange: number = (() => {
      const tempIndex = (exgs || [])
        .slice()
        .reverse()
        .findIndex((e) => e.answer);
      return tempIndex >= 0 ? exgs.length - 1 - tempIndex : tempIndex;
    })();
    if (lastAnsweredExchange <= 1) return 0;
    return get().stateExchanges[lastAnsweredExchange].question?.progress;
  };
  return () =>
    create<PatientStateFields>((set: SetState, get: GetState) => ({
      ...initialState,
      ...demoPaneStateManager(set, get),
      // TODO: ^-- reduce all the state file to smaller chunks like this
      setExchanges: setExchanges(set),
      setExchangeToState: setExchange(set, get),
      completeInfoMessages: completeInfoMessages(set, get),
      setLoading: setLoading(set),
      setLoadingUndo: setLoadingUndo(set),
      setMasterLoading: setMasterLoading(set),
      setConversationDebug: setConversationDebug(set),
      setAgreeOnTerms: setAgreeOnTerms(set),
      setConversationDDResult: setConversationDDResult(set),
      undo: undo(set, get),
      onRetry: onRetry(set, get),
      setAbandoned: setAbandoned(set, get),
      onAnswer: onAnswer(set, get),
      init: init(set, get),
      conversationStatus: driver.getConversationStatus(),
      setShowFeedbackPopup: setShowFeedbackPopup(set),
      computed: {
        get chiefComplaint() {
          return chiefComplaint(get);
        },
        get progress() {
          return progress(get);
        },
        get exchanges() {
          return exchanges(get);
        },
        get age() {
          return age(get);
        },
        get conversation() {
          return get().conversationDebug;
        },
        get caseId() {
          return driver.getCaseId();
        },
        get wasEndOfChatPromptSuggested() {
          return exchanges(get)?.some?.((e) =>
            e?.question?.question?.suggestions?.some?.(
              (s) => s.suggestion.id === END_OF_CHAT_PROMPT_FEATURE_ID,
            ),
          );
        },
        get isEndChatQuestionCurrentlyDisplayed() {
          const lastExchange = exchanges(get)
            ?.slice(-1)
            ?.pop?.();
          return (
            lastExchange?.info?.length >
              0 /* If there are info messages some might be ending the chat, don't allow skip during this time */ ||
            (!get().elementIndex &&
              lastExchange?.question?.question?.questionType ===
                ConversationQuestionType.Final)
          );
        },
      },
      finishConversation,
    }));
};

const qfsToAnswerText = (qfs, type = null) => {
  if (!qfs || !qfs.length) return "";

  const filteredText = featuresToMultiLine(
    qfs.filter((q) => !q.negate && !q.skip),
    ",",
  );

  // If they were all negated or skipped return "None of the above"
  if (filteredText === "") {
    return "No";
  }

  const isSingleQualifier =
    qfs.length === 1 &&
    qfs?.[0].qualifiers?.length === 1 &&
    type !== QTypes.MultipleChiefComplaint;

  if (isSingleQualifier) {
    if (qfs?.[0].qualifiers?.[0]?.value?.id === FREE_TEXT_FEATURE_ID)
      return qfs?.[0].qualifiers?.[0]?.freeText;

    if (qfs?.[0].qualifiers?.[0]?.value?.id === RANGE_QUALIFIER_FEATURE_ID)
      return symptomQualifierTxt(qfs[0]).join("");
  }

  if (
    qfs.length === 1 &&
    (type === QTypes.Suggestion || type === QTypes.BackgroundSuggestion)
  )
    return "yes";

  return filteredText.trim();
};
